
import React, { useEffect, useState } from "react";
import axios from "axios";
import Header from "./Header";
import Footer from "./Footer";
import DynamicTitle from "./DynamicTitle";
import LoadingSkeleton from "./LoadingSkeleton";
import { trackEvent } from "../utils/posthog";
import {
  filterCompleted,
  countUnfinished,
  computeStreaks,
  computeHeatmap,
  computeBestLift,
  computeMostUsedTimeOfDay,
  computeTotalMinutes,
  computeMostUsedType,
  computeAvgMoodLift,
  computeMilestones,
} from "../utils/sessionStats";
import {
  HeroStrip,
  WeekHeatmap,
  DetailStatsGrid,
  MilestonesList,
} from "./SessionStats";

function SessionHistory() {
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sortBy, setSortBy] = useState("dateDesc");
  const [activeFilters, setActiveFilters] = useState(new Set());
  const [sessionTypes, setSessionTypes] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [showUnfinishedInList, setShowUnfinishedInList] = useState(false);

  // Map session type for display: ART script title -> friendly name; others as-is
  const getDisplayName = (sessionType) => {
    if (!sessionType) return "Session";
    if (sessionType === "Accelerated Resolution Therapy" || sessionType === "ART") return "Guided Session";
    return sessionType;
  };

  useEffect(() => {
    trackEvent("session_history_viewed");
    const fetchSessions = async () => {
      setSessionsLoading(true);
      try {
        const response = await axios.get("/api/sessions/history", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setSessions(response.data.session_history);
        const types = new Set(response.data.session_history.map(session => session.session_type));
        setSessionTypes(types);
        setActiveFilters(types);
      } catch (err) {
        setError("Error fetching session history");
        console.error("Error fetching history:", err);
      } finally {
        setSessionsLoading(false);
      }
    };
    fetchSessions();
  }, []);

  const sortSessions = (sessionList) => {
    return [...sessionList].sort((a, b) => {
      switch (sortBy) {
        case "dateAsc":
          return new Date(a.session_start) - new Date(b.session_start);
        case "dateDesc":
          return new Date(b.session_start) - new Date(a.session_start);
        case "length":
          const aLength = a.session_end 
            ? new Date(a.session_end) - new Date(a.session_start)
            : 0;
          const bLength = b.session_end 
            ? new Date(b.session_end) - new Date(b.session_start)
            : 0;
          return bLength - aLength;
        default:
          return 0;
      }
    });
  };

  const completedSessions = filterCompleted(sessions);
  const completedCount = completedSessions.length;
  const unfinishedCount = countUnfinished(sessions);
  const streaks = computeStreaks(completedSessions);
  const heatmapCells = computeHeatmap(completedSessions, 26);
  const bestLift = computeBestLift(completedSessions);
  const avgLift = computeAvgMoodLift(completedSessions);
  const totalMinutes = computeTotalMinutes(completedSessions);
  const timeOfDay = computeMostUsedTimeOfDay(completedSessions);
  const mostUsedType = computeMostUsedType(completedSessions);
  const milestones = computeMilestones(completedSessions, streaks.longest);

  const showHero = completedCount >= 1;
  const showDetail = completedCount >= 3;

  if (sessionsLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="flex flex-col min-h-screen">
      <DynamicTitle>History</DynamicTitle>
      <Header />

      <main className="session-history-main flex-grow mx-auto p-4 sm:px-6 lg:px-8 max-w-screen-xl mt-8 mb-8">
        {showHero && (
          <>
            <HeroStrip completedCount={completedCount} streaks={streaks} />
            <WeekHeatmap cells={heatmapCells} />
          </>
        )}
        {showDetail && (
          <DetailStatsGrid
            bestLift={bestLift}
            avgLift={avgLift}
            totalMinutes={totalMinutes}
            timeOfDay={timeOfDay}
            mostUsedType={mostUsedType}
            weeksPracticed={streaks.weeksPracticed}
            unfinishedCount={unfinishedCount}
            getDisplayName={getDisplayName}
          />
        )}
        {showHero && !showDetail && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6 border border-gray-100">
            <p className="text-gray-600 text-sm">
              Complete a few more sessions to see your patterns.
            </p>
          </div>
        )}
        <MilestonesList milestones={milestones} />

        <div className="mb-6">
          <div className="bg-white rounded-lg shadow-md p-6 mb-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Your Session History
                </h1>
                <p className="text-gray-600">
                  Review and track your progress over time
                </p>
              </div>
              <div className="flex flex-wrap gap-3 items-center">
                <select 
                  className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-700 hover:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-colors"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="dateDesc">Newest First</option>
                  <option value="dateAsc">Oldest First</option>
                  <option value="length">By Length</option>
                </select>
                <button
                  onClick={() => setViewMode(viewMode === 'cards' ? 'table' : 'cards')}
                  className="px-4 py-2 text-gray-700 hover:text-accent border border-gray-300 rounded-lg hover:border-accent bg-white shadow-sm hover:shadow-md transition-all"
                >
                  {viewMode === 'cards' ? '📊 Table View' : '🎴 Card View'}
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm text-accent hover:text-primary font-medium transition-colors flex items-center gap-2"
            >
              {showFilters ? '▼ Hide Filters' : '▶ Show Filters'}
            </button>
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h2 className="font-semibold mb-3 text-gray-900">Filter by Session Type</h2>
                <div className="flex gap-4 flex-wrap">
                  {Array.from(sessionTypes).map((type) => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={activeFilters.has(type)}
                        onChange={(e) => {
                          const newFilters = new Set(activeFilters);
                          if (e.target.checked) {
                            newFilters.add(type);
                          } else {
                            newFilters.delete(type);
                          }
                          setActiveFilters(newFilters);
                        }}
                        className="h-4 w-4 text-accent focus:ring-accent border-gray-300 rounded"
                      />
                      <span className="text-gray-700">{getDisplayName(type)}</span>
                    </label>
                  ))}
                </div>
                {unfinishedCount > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer mt-4 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={showUnfinishedInList}
                      onChange={(e) => setShowUnfinishedInList(e.target.checked)}
                      className="h-4 w-4 text-accent focus:ring-accent border-gray-300 rounded"
                    />
                    <span className="text-gray-700 text-sm">
                      Show sessions you started but didn't finish ({unfinishedCount})
                    </span>
                  </label>
                )}
              </div>
            )}
          </div>
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}
        {!error && sessions.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <p className="text-gray-600 text-lg">No sessions found.</p>
            <p className="text-gray-500 text-sm mt-2">
              Start your first session to see your history here.
            </p>
          </div>
        )}
        {!error && sessions.length > 0 && (
          viewMode === 'cards' ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mt-4">
              {sortSessions(sessions)
                .filter(session => activeFilters.has(session.session_type))
                .filter(session => showUnfinishedInList || session.status === "completed")
                .map((session) => {
                  const duration = session.session_end
                    ? Math.round((new Date(session.session_end) - new Date(session.session_start)) / 1000 / 60)
                    : null;
                  return (
                <div
                  className="bg-white border border-gray-200 rounded-lg p-5 shadow-md hover:shadow-lg transition-shadow"
                  key={session.session_id}
                >
                  <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
                    <h2 className="font-semibold text-lg text-gray-900">
                      {getDisplayName(session.session_type)}
                    </h2>
                    <span className="px-2 py-1 text-xs font-medium bg-accent bg-opacity-10 text-accent rounded">
                      {getDisplayName(session.session_type)}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-500 font-medium">Started:</span>{" "}
                      <span className="text-gray-900">
                        {new Date(session.session_start).toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 font-medium">Ended:</span>{" "}
                      <span className="text-gray-900">
                        {session.session_end
                          ? new Date(session.session_end).toLocaleString()
                          : session.status === "abandoned"
                            ? <span className="text-amber-600 italic">Incomplete</span>
                            : "In Progress"}
                      </span>
                    </div>
                    {duration && (
                      <div>
                        <span className="text-gray-500 font-medium">Duration:</span>{" "}
                        <span className="text-gray-900">{duration} minutes</span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                      <div>
                        <span className="text-gray-500 text-xs">Mood Before</span>
                        <div className="text-lg font-semibold text-gray-900">
                          {session.mood_pre || "—"}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500 text-xs">Mood After</span>
                        <div className="text-lg font-semibold text-gray-900">
                          {session.mood_post || "—"}
                        </div>
                      </div>
                    </div>
                    {session.comment_pre && (
                      <div className="pt-2 border-t border-gray-100">
                        <span className="text-gray-500 text-xs">Note Before:</span>
                        <p className="text-gray-700 text-xs mt-1">{session.comment_pre}</p>
                      </div>
                    )}
                    {session.comment_post && (
                      <div className="pt-2">
                        <span className="text-gray-500 text-xs">Note After:</span>
                        <p className="text-gray-700 text-xs mt-1">{session.comment_post}</p>
                      </div>
                    )}
                  </div>
                </div>
              )})}
            </div>
          ) : (
          <div className="mt-4 overflow-x-auto bg-white rounded-lg shadow-md">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="p-4 text-left text-sm font-semibold text-gray-700">Type</th>
                  <th className="p-4 text-left text-sm font-semibold text-gray-700">Start Time</th>
                  <th className="p-4 text-left text-sm font-semibold text-gray-700">End Time</th>
                  <th className="p-4 text-left text-sm font-semibold text-gray-700">Duration</th>
                  <th className="p-4 text-left text-sm font-semibold text-gray-700">Mood Pre</th>
                  <th className="p-4 text-left text-sm font-semibold text-gray-700">Mood Post</th>
                  <th className="p-4 text-left text-sm font-semibold text-gray-700">Notes</th>
                </tr>
              </thead>
              <tbody>
                {sortSessions(sessions)
                  .filter(session => activeFilters.has(session.session_type))
                .filter(session => showUnfinishedInList || session.status === "completed")
                  .map((session) => {
                    const duration = session.session_end
                      ? Math.round((new Date(session.session_end) - new Date(session.session_start)) / 1000 / 60)
                      : null;
                    return (
                  <tr key={session.session_id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="p-4">
                      <span className="px-2 py-1 text-xs font-medium bg-accent bg-opacity-10 text-accent rounded">
                        {getDisplayName(session.session_type)}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-900">{new Date(session.session_start).toLocaleString()}</td>
                    <td className="p-4 text-sm text-gray-900">{session.session_end ? new Date(session.session_end).toLocaleString() : session.status === "abandoned" ? <span className="text-amber-600 italic">Incomplete</span> : <span className="text-gray-500 italic">In Progress</span>}</td>
                    <td className="p-4 text-sm text-gray-900">{duration ? `${duration} min` : "—"}</td>
                    <td className="p-4 text-sm font-medium text-gray-900">{session.mood_pre || "—"}</td>
                    <td className="p-4 text-sm font-medium text-gray-900">{session.mood_post || "—"}</td>
                    <td className="p-4 text-sm text-gray-600">
                      {session.comment_pre || session.comment_post ? (
                        <div className="space-y-1">
                          {session.comment_pre && <div className="text-xs"><span className="font-medium">Before:</span> {session.comment_pre}</div>}
                          {session.comment_post && <div className="text-xs"><span className="font-medium">After:</span> {session.comment_post}</div>}
                        </div>
                      ) : "—"}
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          </div>
          )
        )}
      </main>

      <Footer />
    </div>
  );
}

export default SessionHistory;
